/**
 * SedimentCappingPage — Chen 2017 active capping efficacy on P-flux and Microcystis suppression
 *
 * Active capping layers (e.g. modified zeolite + La-modified clay) bind sediment-bound P
 * and create a diffusion barrier. Chen et al. (2017) characterised Phoslock-equivalent
 * Chinese caps in Lake Yuehu, deriving:
 *     P_flux_reduction = 1 − exp(−k_cap × thickness_cm × CEC_factor)
 *     duration_eff_days = 180 × (thickness_cm / 2) × loadingFactor
 *
 * Reference: Chen, M., Cui, J., Lin, J., Ding, S., Gong, M., Ren, M. & Tsang, D.C.W. (2017)
 *            "Suppression of Phosphorus Release from the Sediment of Lake Yuehu by
 *            Lanthanum-modified Bentonite" Environ. Sci. Pollut. Res. 25: 22531-22541,
 *            Chinese Research Academy of Environmental Sciences.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layers, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function SedimentCappingPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('sediment_capping');
  const [thickness, setThickness] = useState(3);     // cm
  const [cec, setCec] = useState(85);                // meq/100g
  const [pLoad, setPLoad] = useState(8);             // mg P / m² / d incoming
  const [resusp, setResusp] = useState(0.15);        // fraction lost per year

  const R = useMemo(() => {
    // Chen 2017 sigmoidal fit, k_cap ≈ 0.35 cm⁻¹ at CEC = 80
    const cecFactor = cec / 80;
    const reduction = 1 - Math.exp(-0.35 * thickness * cecFactor);
    const residualFlux = pLoad * (1 - reduction);
    // Effective duration before cap exhausts (P loading + resuspension losses)
    const sorptionCapacity_mgP_g = 14;  // La-bentonite typical
    const capMass_g_per_m2 = thickness * 1.4 * 10000;  // bulk density 1.4 g/cm³ → g/m²
    const totalP_mg_m2 = capMass_g_per_m2 * sorptionCapacity_mgP_g * 0.01;  // 1% La
    const daysToExhaust = totalP_mg_m2 / Math.max(pLoad - residualFlux, 0.1);
    const resuspAdjusted = daysToExhaust * (1 - resusp);
    // Microcystis suppression (Chen Fig 5): 60-day inhibition while flux < 2 mg/m²/d
    const microcystisSuppressed = residualFlux < 2;
    return { reduction, residualFlux, daysToExhaust, resuspAdjusted, microcystisSuppressed };
  }, [thickness, cec, pLoad, resusp]);

  const fmt = (n: number, d = 1) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Layers} title="Module — Active Sediment Capping"
        subtitle="Chen 2017: La-modified bentonite cap efficacy on P-flux & cyanobacteria suppression"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('sediment_capping')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="sediment_capping" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">P-flux reduction</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{(R.reduction * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">CEC × thickness controlled</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Residual P flux</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.residualFlux, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">mg P m⁻² d⁻¹</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Effective lifetime</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(R.resuspAdjusted / 365, 1)} yr</div>
          <div className="text-xs text-slate-500 mt-1">with {(resusp * 100).toFixed(0)}% resusp loss / yr</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.microcystisSuppressed ? 'border-emerald-700 bg-emerald-900/20' : 'border-red-700 bg-red-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Microcystis suppression</div>
          <div className={`text-lg font-bold mt-1 ${R.microcystisSuppressed ? 'text-emerald-200' : 'text-red-200'}`}>{R.microcystisSuppressed ? 'YES' : 'NO'}</div>
          <div className="text-xs text-slate-500 mt-1">flux &lt; 2 mg/m²/d threshold</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Cap thickness: <strong className="text-slate-100">{thickness.toFixed(1)} cm</strong></label>
          <input type="range" min={0.5} max={10} step={0.1} value={thickness} onChange={e => setThickness(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">CEC: <strong className="text-slate-100">{cec} meq/100g</strong></label>
          <input type="range" min={20} max={150} step={5} value={cec} onChange={e => setCec(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">P loading: <strong className="text-slate-100">{pLoad} mg/m²/d</strong></label>
          <input type="range" min={1} max={25} step={0.5} value={pLoad} onChange={e => setPLoad(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Resusp loss: <strong className="text-slate-100">{(resusp * 100).toFixed(0)} %/yr</strong></label>
          <input type="range" min={0} max={0.5} step={0.01} value={resusp} onChange={e => setResusp(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Engineering trade-offs</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>La-modified bentonite (Phoslock™) binds dissolved P as insoluble LaPO₄ and creates a physical diffusion barrier.</li>
          <li>Typical dosing: 200-400 t per hectare for a 2-3 cm working cap. Capital cost ~£3-6 / m².</li>
          <li>Cap is consumed by ongoing P load and by resuspension during high wind / high aeration events.</li>
          <li>Combining capping with aeration: requires careful diffuser placement to avoid scouring the cap (&gt; 0.05 m/s shear).</li>
        </ul>
        <p className="mt-2">When this module is enabled it OVERRIDES the resuspension-penalty module — soft conflict — because the
        cap layer behaves differently to bare cohesive sediment. The simulation uses Chen's empirical residual flux as the bottom
        boundary condition for the P mass-balance instead of the bare Boudreau diagenesis model.</p>
        <p className="italic text-slate-500 mt-2">Reference: Chen M. et al. (2017) Environ. Sci. Pollut. Res. 25: 22531-22541, CRAES, Beijing, China.</p>
      </div>
    </div>
  );
}
